import { Suspense } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO, generateBreadcrumbSchema } from '../../utils/seo';

export default function StationsPage() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  // Schema.org JSON-LD for Stations page
  const stationsSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${siteUrl}/stations#webpage`,
    url: `${siteUrl}/stations`,
    name: 'Toutes nos Destinations Aéroports, Gares  Hôpitaux',
    description: 'Transferts privés vers 5 stations de ski (Val Thorens, Courchevel, Méribel), 7 aéroports, 7 gares SNCF et 4 hôpitaux dans les Alpes. Service premium 24/7.',
    inLanguage: 'fr-FR',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: 'Voyage Elite'
    },
    breadcrumb: generateBreadcrumbSchema([
      { name: 'Accueil', url: '/' },
      { name: 'Destinations', url: '/stations' }
    ]),
    mainEntity: {
      '@type': 'ItemList',
      name: 'Destinations de Transfert',
      description: 'Liste complète des destinations desservies : stations de ski, aéroports, gares et hôpitaux',
      numberOfItems: 23,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@type': 'SkiResort',
            name: 'Val Thorens',
            description: 'Plus haute station d\'Europe, ski glacier',
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '45.2975',
              longitude: '6.5803'
            }
          }
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@type': 'SkiResort',
            name: 'Courchevel',
            description: 'Station de prestige, plus grand domaine',
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '45.4167',
              longitude: '6.6333'
            }
          }
        },
        {
          '@type': 'ListItem',
          position: 3,
          item: {
            '@type': 'SkiResort',
            name: 'Méribel',
            description: 'Architecture savoyarde, centre 3 Vallées',
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '45.4006',
              longitude: '6.5669'
            }
          }
        },
        {
          '@type': 'ListItem',
          position: 4,
          item: {
            '@type': 'Airport',
            name: 'Aéroport de Genève',
            iataCode: 'GVA',
            description: 'Hub international, accès Alpes'
          }
        },
        {
          '@type': 'ListItem',
          position: 5,
          item: {
            '@type': 'Airport',
            name: 'Aéroport Lyon Saint-Exupéry',
            iataCode: 'LYS',
            description: 'Hub principal Rhône-Alpes'
          }
        },
        {
          '@type': 'ListItem',
          position: 6,
          item: {
            '@type': 'TrainStation',
            name: 'Gare de Moutiers',
            description: 'Porte des 3 Vallées, navettes stations'
          }
        },
        {
          '@type': 'ListItem',
          position: 7,
          item: {
            '@type': 'Hospital',
            name: 'Hôpital d\'Albertville',
            description: 'Services complets, Tarentaise'
          }
        }
      ]
    },
    about: {
      '@type': 'Service',
      name: 'Service de Transfert Complet dans les Alpes',
      description: 'Service de transfert privé couvrant 5 stations de ski des 3 Vallées, 7 aéroports majeurs, 7 gares SNCF stratégiques et 4 hôpitaux principaux. Service 24/7 avec chauffeurs professionnels et véhicules Mercedes premium.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Voyage Elite',
        telephone: '+33605606006'
      },
      areaServed: [
        { '@type': 'Place', name: 'Val Thorens' },
        { '@type': 'Place', name: 'Courchevel' },
        { '@type': 'Place', name: 'Méribel' },
        { '@type': 'Place', name: 'Les Menuires' },
        { '@type': 'Place', name: 'Saint-Martin-de-Belleville' },
        { '@type': 'City', name: 'Genève' },
        { '@type': 'City', name: 'Lyon' },
        { '@type': 'City', name: 'Chambéry' },
        { '@type': 'City', name: 'Grenoble' }
      ]
    }
  };

  useSEO({
    title: 'Toutes nos Destinations Aéroports, Gares  Hôpitaux',
    description: 'Transferts privés vers 5 stations de ski (Val Thorens, Courchevel, Méribel), 7 aéroports, 7 gares SNCF et 4 hôpitaux dans les Alpes. Service premium 24/7.',
    keywords: 'transfert Val Thorens, navette Courchevel, transport aéroport Genève, gare Moutiers, hôpital Albertville, VTC Méribel',
    canonical: '/stations',
    schema: stationsSchema,
  });

  const skiStations = [
    { 
      name: 'Val Thorens', 
      altitude: '2300m', 
      info: 'Plus haute station d\'Europe, ski glacier',
      icon: 'ri-mountain-snow-line',
      gradient: 'from-blue-500 to-cyan-600'
    },
    { 
      name: 'Les Menuires', 
      altitude: '1850m', 
      info: 'Cœur des 3 Vallées, rapport qualité-prix',
      icon: 'ri-mountain-snow-line',
      gradient: 'from-violet-500 to-purple-600'
    },
    { 
      name: 'Saint-Martin-de-Belleville', 
      altitude: '1450m', 
      info: 'Village authentique, accès 3 Vallées',
      icon: 'ri-mountain-snow-line',
      gradient: 'from-emerald-500 to-green-600'
    },
    { 
      name: 'Méribel', 
      altitude: '1450m', 
      info: 'Architecture savoyarde, centre 3 Vallées',
      icon: 'ri-mountain-snow-line',
      gradient: 'from-rose-500 to-pink-600'
    },
    { 
      name: 'Courchevel', 
      altitude: '1850m', 
      info: 'Station de prestige, plus grand domaine',
      icon: 'ri-mountain-snow-line',
      gradient: 'from-amber-500 to-orange-600'
    },
  ];

  const airports = [
    { 
      name: 'Aéroport d\'Annecy', 
      code: 'NCY',
      distance: '~100km',
      info: 'Aéroport régional, proximité stations',
      icon: 'ri-plane-line',
      gradient: 'from-sky-500 to-blue-600'
    },
    { 
      name: 'Aéroport Chambéry', 
      code: 'CMF',
      distance: '~80km',
      info: 'Aéroport de montagne, accès direct',
      icon: 'ri-plane-line',
      gradient: 'from-indigo-500 to-purple-600'
    },
    { 
      name: 'Aéroport de Genève', 
      code: 'GVA',
      distance: '~180km',
      info: 'Hub international, accès Alpes',
      icon: 'ri-plane-line',
      gradient: 'from-cyan-500 to-teal-600'
    },
    { 
      name: 'Aéroport de Grenoble', 
      code: 'GNB',
      distance: '~150km',
      info: 'Alpes-Isère, porte du Sud',
      icon: 'ri-plane-line',
      gradient: 'from-green-500 to-emerald-600'
    },
    { 
      name: 'Aéroport Lyon Saint-Exupéry', 
      code: 'LYS',
      distance: '~190km',
      info: 'Hub principal Rhône-Alpes',
      icon: 'ri-plane-line',
      gradient: 'from-orange-500 to-amber-600'
    },
    { 
      name: 'Aéroport de Turin Caselle', 
      code: 'TRN',
      distance: '~200km',
      info: 'Italie, accès vallées alpines',
      icon: 'ri-plane-line',
      gradient: 'from-red-500 to-rose-600'
    },
    { 
      name: 'Aéroport de Milan Malpensa', 
      code: 'MXP',
      distance: '~280km',
      info: 'Hub international italien',
      icon: 'ri-plane-line',
      gradient: 'from-pink-500 to-fuchsia-600'
    },
  ];

  const trainStations = [
    { 
      name: 'Gare de Moutiers', 
      type: 'SNCF',
      distance: '~25km',
      info: 'Porte des 3 Vallées, navettes stations',
      icon: 'ri-train-line',
      gradient: 'from-blue-600 to-indigo-700'
    },
    { 
      name: 'Gare TGV Albertville', 
      type: 'TGV',
      distance: '~50km',
      info: 'TGV direct Paris, accès Tarentaise',
      icon: 'ri-train-line',
      gradient: 'from-violet-600 to-purple-700'
    },
    { 
      name: 'Gare de Chambéry – Challes-les-Eaux', 
      type: 'SNCF',
      distance: '~90km',
      info: 'Connexion nationale, Savoie',
      icon: 'ri-train-line',
      gradient: 'from-cyan-600 to-blue-700'
    },
    { 
      name: 'Gare TGV Annecy', 
      type: 'TGV',
      distance: '~110km',
      info: 'TGV direct, Haute-Savoie',
      icon: 'ri-train-line',
      gradient: 'from-emerald-600 to-green-700'
    },
    { 
      name: 'Gare de Genève Cornavin', 
      type: 'International',
      distance: '~180km',
      info: 'Gare principale Suisse',
      icon: 'ri-train-line',
      gradient: 'from-teal-600 to-cyan-700'
    },
    { 
      name: 'Gare de Lyon Part-Dieu', 
      type: 'TGV',
      distance: '~200km',
      info: 'Hub TGV national',
      icon: 'ri-train-line',
      gradient: 'from-orange-600 to-amber-700'
    },
    { 
      name: 'Gare de Lyon Saint-Exupéry', 
      type: 'TGV',
      distance: '~190km',
      info: 'Gare aéroport, connexion directe',
      icon: 'ri-train-line',
      gradient: 'from-rose-600 to-pink-700'
    },
  ];

  const hospitals = [
    { 
      name: 'Hôpital de Moutiers', 
      type: 'Centre Hospitalier',
      distance: '~25km',
      info: 'Urgences, proximité stations',
      icon: 'ri-hospital-line',
      gradient: 'from-red-500 to-rose-600'
    },
    { 
      name: 'Hôpital d\'Albertville', 
      type: 'Centre Hospitalier',
      distance: '~50km',
      info: 'Services complets, Tarentaise',
      icon: 'ri-hospital-line',
      gradient: 'from-pink-500 to-fuchsia-600'
    },
    { 
      name: 'Hôpital de Bourg St Maurice', 
      type: 'Centre Hospitalier',
      distance: '~45km',
      info: 'Urgences montagne, Haute-Tarentaise',
      icon: 'ri-hospital-line',
      gradient: 'from-orange-500 to-red-600'
    },
    { 
      name: 'Hôpital de Chambéry', 
      type: 'CHU',
      distance: '~90km',
      info: 'CHU principal Savoie',
      icon: 'ri-hospital-line',
      gradient: 'from-rose-500 to-red-600'
    },
  ];

  const handleReservation = (destination: string) => {
    window.REACT_APP_NAVIGATE(`/reservation?destination=${encodeURIComponent(destination)}`);
  };

  return (
    <Suspense fallback={<div>Chargement...</div>}>
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 px-6 lg:px-12 bg-gradient-to-br from-night-blue via-slate-900 to-night-blue relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-alpine-gold rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="font-playfair text-5xl lg:text-6xl font-bold text-white mb-6">
                Toutes nos <span className="text-alpine-gold">Destinations</span>
              </h1>
              <p className="text-slate-300 font-inter text-xl max-w-3xl mx-auto leading-relaxed mb-8">
                Transferts privés vers stations de ski, aéroports, gares SNCF et hôpitaux dans les Alpes
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                  <span className="text-alpine-gold font-inter font-bold text-base">5 Stations</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                  <span className="text-alpine-gold font-inter font-bold text-base">7 Aéroports</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                  <span className="text-alpine-gold font-inter font-bold text-base">7 Gares</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                  <span className="text-alpine-gold font-inter font-bold text-base">4 Hôpitaux</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section ref={ref} className="py-24 px-6 lg:px-12 bg-gradient-to-br from-slate-50 via-white to-slate-50">
          <div className="max-w-7xl mx-auto">
            
            {/* Ski Stations */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 }}
              className="mb-20"
            >
              <h2 className="font-inter text-3xl font-bold text-night-blue mb-8 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg">
                  <i className="ri-mountain-snow-line text-white text-2xl"></i>
                </div>
                <strong>Stations de Ski</strong>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {skiStations.map((station, index) => (
                  <motion.div
                    key={station.name}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    whileHover={{ y: -6, scale: 1.02 }}
                    className="group bg-white rounded-2xl p-6 border-2 border-slate-200 hover:border-alpine-gold hover:shadow-2xl transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${station.gradient} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg`}>
                        <i className={`${station.icon} text-white text-2xl`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-night-blue font-inter text-lg font-bold mb-1">
                          <strong>{station.name}</strong>
                        </h3>
                        <p className="text-alpine-gold font-inter text-sm font-semibold mb-2">
                          {station.altitude}
                        </p>
                      </div>
                    </div>
                    <p className="text-slate-600 font-inter text-sm leading-relaxed mb-4">
                      {station.info}
                    </p>
                    <button
                      onClick={() => handleReservation(station.name)}
                      className="w-full flex items-center justify-between pt-4 border-t border-slate-200 group/btn hover:bg-alpine-gold/5 -mx-6 px-6 -mb-6 pb-6 rounded-b-2xl transition-all"
                    >
                      <span className="text-alpine-gold font-inter text-sm font-semibold flex items-center gap-2">
                        <i className="ri-car-line text-base"></i>
                        Réserver
                      </span>
                      <i className="ri-arrow-right-line text-alpine-gold text-lg group-hover/btn:translate-x-2 transition-all"></i>
                    </button>
                    <a
                      href="https://wa.me/33605606006"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 pt-3 pb-3 bg-green-500 text-white rounded-xl font-inter font-semibold hover:bg-green-600 transition-all -mx-6 px-6 -mb-6 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-line text-lg"></i>
                      WhatsApp
                    </a>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Airports */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4 }}
              className="mb-20"
            >
              <h2 className="font-inter text-3xl font-bold text-night-blue mb-8 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-alpine-gold to-amber-600 flex items-center justify-center shadow-lg">
                  <i className="ri-plane-line text-white text-2xl"></i>
                </div>
                <strong>Aéroports</strong>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {airports.map((airport, index) => (
                  <motion.div
                    key={airport.code}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    whileHover={{ y: -6, scale: 1.02 }}
                    className="group bg-white rounded-2xl p-6 border-2 border-slate-200 hover:border-alpine-gold hover:shadow-2xl transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${airport.gradient} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg`}>
                        <i className={`${airport.icon} text-white text-2xl`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-night-blue font-inter text-base font-bold mb-1">
                          <strong>{airport.name}</strong>
                        </h3>
                        <p className="text-alpine-gold font-inter text-sm font-semibold mb-2">
                          {airport.code}
                        </p>
                        <p className="text-slate-500 font-inter text-xs">
                          {airport.distance}
                        </p>
                      </div>
                    </div>
                    <p className="text-slate-600 font-inter text-sm leading-relaxed mb-4">
                      {airport.info}
                    </p>
                    <button
                      onClick={() => handleReservation(airport.name)}
                      className="w-full flex items-center justify-between pt-4 border-t border-slate-200 group/btn hover:bg-alpine-gold/5 -mx-6 px-6 -mb-6 pb-6 rounded-b-2xl transition-all"
                    >
                      <span className="text-alpine-gold font-inter text-sm font-semibold flex items-center gap-2">
                        <i className="ri-car-line text-base"></i>
                        Réserver
                      </span>
                      <i className="ri-arrow-right-line text-alpine-gold text-lg group-hover/btn:translate-x-2 transition-all"></i>
                    </button>
                    <a
                      href="https://wa.me/33605606006"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 pt-3 pb-3 bg-green-500 text-white rounded-xl font-inter font-semibold hover:bg-green-600 transition-all -mx-6 px-6 -mb-6 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-line text-lg"></i>
                      WhatsApp
                    </a>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Train Stations */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 }}
              className="mb-20"
            >
              <h2 className="font-inter text-3xl font-bold text-night-blue mb-8 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                  <i className="ri-train-line text-white text-2xl"></i>
                </div>
                <strong>Gares SNCF</strong>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {trainStations.map((station, index) => (
                  <motion.div
                    key={station.name}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.7 + index * 0.1 }}
                    whileHover={{ y: -6, scale: 1.02 }}
                    className="group bg-white rounded-2xl p-6 border-2 border-slate-200 hover:border-alpine-gold hover:shadow-2xl transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${station.gradient} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg`}>
                        <i className={`${station.icon} text-white text-2xl`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-night-blue font-inter text-base font-bold mb-1">
                          <strong>{station.name}</strong>
                        </h3>
                        <p className="text-alpine-gold font-inter text-sm font-semibold mb-2">
                          {station.type}
                        </p>
                        <p className="text-slate-500 font-inter text-xs">
                          {station.distance}
                        </p>
                      </div>
                    </div>
                    <p className="text-slate-600 font-inter text-sm leading-relaxed mb-4">
                      {station.info}
                    </p>
                    <button
                      onClick={() => handleReservation(station.name)}
                      className="w-full flex items-center justify-between pt-4 border-t border-slate-200 group/btn hover:bg-alpine-gold/5 -mx-6 px-6 -mb-6 pb-6 rounded-b-2xl transition-all"
                    >
                      <span className="text-alpine-gold font-inter text-sm font-semibold flex items-center gap-2">
                        <i className="ri-car-line text-base"></i>
                        Réserver
                      </span>
                      <i className="ri-arrow-right-line text-alpine-gold text-lg group-hover/btn:translate-x-2 transition-all"></i>
                    </button>
                    <a
                      href="https://wa.me/33605606006"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 pt-3 pb-3 bg-green-500 text-white rounded-xl font-inter font-semibold hover:bg-green-600 transition-all -mx-6 px-6 -mb-6 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-line text-lg"></i>
                      WhatsApp
                    </a>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Hospitals */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8 }}
              className="mb-20"
            >
              <h2 className="font-inter text-3xl font-bold text-night-blue mb-8 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-lg">
                  <i className="ri-hospital-line text-white text-2xl"></i>
                </div>
                <strong>Hôpitaux</strong>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {hospitals.map((hospital, index) => (
                  <motion.div
                    key={hospital.name}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.9 + index * 0.1 }}
                    whileHover={{ y: -6, scale: 1.02 }}
                    className="group bg-white rounded-2xl p-6 border-2 border-slate-200 hover:border-alpine-gold hover:shadow-2xl transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${hospital.gradient} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg`}>
                        <i className={`${hospital.icon} text-white text-2xl`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-night-blue font-inter text-base font-bold mb-1">
                          <strong>{hospital.name}</strong>
                        </h3>
                        <p className="text-alpine-gold font-inter text-sm font-semibold mb-2">
                          {hospital.type}
                        </p>
                        <p className="text-slate-500 font-inter text-xs">
                          {hospital.distance}
                        </p>
                      </div>
                    </div>
                    <p className="text-slate-600 font-inter text-sm leading-relaxed mb-4">
                      {hospital.info}
                    </p>
                    <button
                      onClick={() => handleReservation(hospital.name)}
                      className="w-full flex items-center justify-between pt-4 border-t border-slate-200 group/btn hover:bg-alpine-gold/5 -mx-6 px-6 -mb-6 pb-6 rounded-b-2xl transition-all"
                    >
                      <span className="text-alpine-gold font-inter text-sm font-semibold flex items-center gap-2">
                        <i className="ri-car-line text-base"></i>
                        Réserver
                      </span>
                      <i className="ri-arrow-right-line text-alpine-gold text-lg group-hover/btn:translate-x-2 transition-all"></i>
                    </button>
                    <a
                      href="https://wa.me/33605606006"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 pt-3 pb-3 bg-green-500 text-white rounded-xl font-inter font-semibold hover:bg-green-600 transition-all -mx-6 px-6 -mb-6 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-line text-lg"></i>
                      WhatsApp
                    </a>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* SEO Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 1 }}
              className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl border border-slate-200"
            >
              <h2 className="font-inter text-2xl font-bold text-night-blue mb-6">
                <strong>Service de Transfert Complet dans les Alpes</strong>
              </h2>
              <div className="grid md:grid-cols-2 gap-6 text-slate-700 font-inter text-sm leading-relaxed">
                <div>
                  <p className="mb-4">
                    Notre service de <strong>transfert privé</strong> couvre l'ensemble des <strong>stations de ski des 3 Vallées</strong> : Val Thorens, Les Menuires, Saint-Martin-de-Belleville, Méribel et Courchevel.
                  </p>
                  <p className="mb-4">
                    Nous assurons également les <strong>transferts depuis 7 aéroports</strong> majeurs (Genève, Lyon, Chambéry, Grenoble, Annecy, Turin, Milan) et <strong>7 gares SNCF</strong> stratégiques.
                  </p>
                </div>
                <div>
                  <p className="mb-4">
                    Pour les <strong>urgences médicales</strong>, nous proposons des transferts rapides vers les <strong>4 hôpitaux principaux</strong> de la région : Moutiers, Albertville, Bourg-Saint-Maurice et Chambéry.
                  </p>
                  <p>
                    Service <strong>24/7</strong> avec <strong>chauffeurs professionnels</strong> et <strong>véhicules Mercedes premium</strong>. Réservation en ligne instantanée.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 1.2 }}
              className="mt-16 text-center"
            >
              <div className="bg-gradient-to-br from-night-blue to-slate-900 rounded-3xl p-12">
                <h3 className="font-playfair text-3xl lg:text-4xl font-bold text-white mb-4">
                  Réservez Votre Transfert Maintenant
                </h3>
                <p className="text-slate-300 font-inter text-base mb-8">
                  Disponible 24/7 • Tarifs transparents • Véhicules premium
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/reservation')}
                    className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-alpine-gold text-night-blue rounded-xl font-inter font-medium hover:bg-white transition-all shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-calendar-line text-xl"></i>
                    Réserver en Ligne
                  </button>
                  <a
                    href="https://wa.me/33605606006"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-green-500 text-white rounded-xl font-inter font-medium hover:bg-green-600 transition-all shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-2xl"></i>
                    WhatsApp
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}
